import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { apiError } from "@/lib/apiError";
import { Loader2, Images } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/lib/i18n";
import { AppHeader } from "@/components/AppHeader";import { SupportButton } from "@/components/SupportButton";
import { TrialCountdown } from "@/components/TrialCountdown";
import { withRowKeys } from "@/lib/rowKey";
import { PromptDialog, ConfirmDialog } from "@/components/Dialog";
import { ProjectFilters } from "@/components/dashboard/ProjectFilters";
import { BatchActionBar } from "@/components/dashboard/BatchActionBar";
import { ProjectCard } from "@/components/dashboard/ProjectCard";
import { DashboardHeading } from "@/components/dashboard/DashboardHeading";
import { NewProjectDialog } from "@/components/dashboard/NewProjectDialog";
import { DeleteProjectDialog } from "@/components/dashboard/DeleteProjectDialog";
import { logError } from "@/lib/log";

const emptyForm = {
  code: "",
  title: "",
  client_name: "",
  client_email: "",
  client_phone: "",
  extra_edit_price: "",
  package_price: "",
  package_label: "",
  drive_link: "",
  edit_quota: 10,
  allow_extra_edits: true,
  max_extra_edits: 20,
  print_sizes: ["4x6 in", "8x12 in", "12x18 in"],
  print_quota_items: [],
  module: "edit_print",
  use_sample_photos: false,
};

export default function Dashboard() {
  const { isOwner } = useAuth();
  const { t } = useI18n();
  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [busy, setBusy] = useState(false);
  const [trial, setTrial] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [query, setQuery] = useState("");
  const [sort, setSort] = useState("newest");
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [deleteText, setDeleteText] = useState("");
  const [folders, setFolders] = useState([]);
  const [folder, setFolder] = useState("all");
  const [picked, setPicked] = useState([]);
  const [folderDialog, setFolderDialog] = useState(null);   // {mode:'create'|'rename', folder}
  const [folderToDelete, setFolderToDelete] = useState(null);
  const [reminding, setReminding] = useState("");
  const [printCatalog, setPrintCatalog] = useState({});

  const remind = async (p) => {
    setReminding(p.project_id);
    try {
      const res = await api.post(`/projects/${p.project_id}/remind`);
      toast.success(res.data.email_status === "sent" ? t("reminder_sent") : t("reminder_wa_only"));
      if (res.data.whatsapp_url) window.open(res.data.whatsapp_url, "_blank", "noopener");
      await load();
    } catch (err) {
      toast.error(apiError(err, t("reminder_failed")));
    } finally {
      setReminding("");
    }
  };

  const saveFolder = async (name) => {
    const editing = folderDialog?.folder;
    setFolderDialog(null);
    try {
      if (editing) await api.put(`/studio/folders/${editing.folder_id}`, { name });
      else await api.post("/studio/folders", { name });
      await loadFolders();
    } catch (err) {
      toast.error(apiError(err, "Could not save folder"));
    }
  };

  const loadFolders = useCallback(async () => {
    try {
      const res = await api.get("/studio/folders");
      setFolders(res.data);
    } catch (err) {
      logError("Could not load folders", err);
    }
  }, []);

  const removeFolder = async (f) => {
    setFolderToDelete(null);
    try {
      await api.delete(`/studio/folders/${f.folder_id}`);
      if (folder === f.folder_id) setFolder("all");
      await Promise.all([loadFolders(), load()]);
    } catch (err) {
      toast.error(apiError(err, "Could not delete folder"));
    }
  };

  const togglePick = (id) =>
    setPicked((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id]));

  const batchMove = async (folderId) => {
    try {
      await api.post("/projects/batch/move", { project_ids: picked, folder_id: folderId || null });
      setPicked([]);
      await load();
      toast.success(t("batch_moved"));
    } catch (err) {
      toast.error(apiError(err, "Could not move"));
    }
  };

  const batchDelete = async () => {
    try {
      await api.post("/projects/batch/delete", { project_ids: picked });
      setPicked([]);
      setConfirmDelete(null);
      setDeleteText("");
      await load();
      toast.success(t("batch_deleted"));
    } catch (err) {
      toast.error(apiError(err, "Could not delete"));
    }
  };

  const visible = projects
    .filter((p) => (folder === "all" ? true : folder === "none" ? !p.folder_id : p.folder_id === folder))
    .filter((p) => {
      const q = query.trim().toLowerCase();
      if (!q) return true;
      return `${p.title || ""} ${p.code || ""} ${p.client_name || ""}`.toLowerCase().includes(q);
    })
    .sort((a, b) =>
      sort === "alpha"
        ? (a.title || "").localeCompare(b.title || "")
        : (b.created_at || "").localeCompare(a.created_at || "")
    );

  const load = useCallback(async () => {
    try {
      const res = await api.get("/projects");
      setProjects(res.data);
      try {
        const s = await api.get("/studio");
        setTrial(s.data.trial?.is_trial ? s.data.trial : null);
        setPrintCatalog(s.data.pricing?.print_prices || {});
      } catch (err) {
        logError("Could not load studio", err);
      }
      try {
        const tpl = await api.get("/studio/templates");
        setTemplates(tpl.data);
      } catch (err) {
        logError("Could not load templates", err);
      }
      await loadFolders();
    } catch (err) {
      toast.error(apiError(err, "Could not load projects"));
    } finally {
      setLoading(false);
    }
  }, [loadFolders]);

  useEffect(() => {
    load();
  }, [load]);

  const create = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const res = await api.post("/projects", {
        ...form,
        client_email: form.client_email || null,
        edit_quota: Number(form.edit_quota),
        max_extra_edits: Number(form.max_extra_edits),
        pricing_override: form.extra_edit_price ? { extra_edit_price: Number(form.extra_edit_price) } : {},
        package_price: form.package_price === "" || form.package_price == null ? null : Number(form.package_price),
        print_sizes: form.print_sizes,
        print_quota_items: form.print_quota_items
          .filter((i) => i.size.trim())
          .map(({ _k, ...i }) => ({ ...i, qty: Number(i.qty) })),
      });
      if (res.data.drive_error) toast.warning(res.data.drive_error);
      else toast.success(`Gallery created with ${res.data.photos.length} photos`);
      if (res.data.client_access_code)
        toast.info(`Client access code: ${res.data.client_access_code}`, { duration: 10000 });
      setOpen(false);
      setForm(emptyForm);
      navigate(`/projects/${res.data.project_id}`);
    } catch (err) {
      toast.error(apiError(err, "Could not create the project"));
    } finally {
      setBusy(false);
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/projects/${id}`);
      setProjects((p) => p.filter((x) => x.project_id !== id));
      setConfirmDelete(null);
      setDeleteText("");
      toast.success(t("project_deleted"));
    } catch (err) {
      toast.error(apiError(err, "Could not delete"));
    }
  };

  /**
   * One-tap package prices, gathered from whatever the studio already uses — saved
   * templates AND recent galleries — so the list survives deleting every template.
   */
  const packagePresets = useMemo(() => {
    const out = [];
    const add = (price, label) => {
      const n = Number(price || 0);
      if (n > 0 && !out.some((p) => p.price === n)) out.push({ price: n, label: label || String(n) });
    };
    templates.forEach((tpl) => add(tpl.package_price, tpl.name));
    projects.forEach((p) => add(p.package_price, p.package_label || p.title));
    return out.sort((a, b) => a.price - b.price).slice(0, 8);
  }, [templates, projects]);


  const applyTemplate = (tpl) =>
    setForm((f) => ({
      ...f,
      edit_quota: tpl.edit_quota,
      allow_extra_edits: tpl.allow_extra_edits,
      max_extra_edits: tpl.max_extra_edits,
      extra_edit_price: tpl.extra_edit_price ?? "",
      package_price: tpl.package_price ?? "",
      package_label: tpl.package_price ? tpl.name : "",
      print_sizes: tpl.print_sizes || [],
      print_quota_items: (tpl.print_quota_items || []).length
        ? withRowKeys(tpl.print_quota_items.map((i) => ({ size: i.size, qty: i.qty, frame_required: !!i.frame_required })))
        : f.print_quota_items,
    }));

  const setQuotaItem = (idx, patch) =>
    setForm((f) => ({
      ...f,
      print_quota_items: f.print_quota_items.map((i, n) => (n === idx ? { ...i, ...patch } : i)),
    }));

  return (
    <div className="min-h-screen bg-[#050505] text-[#F3F3F1] grain" data-testid="dashboard-page">
      <AppHeader />

      <main className="max-w-7xl mx-auto px-5 lg:px-12 py-12 lg:py-16">
        <DashboardHeading trial={trial} projectCount={projects.length} onNew={() => setOpen(true)} />

        {!loading && (
          <ProjectFilters
            folders={folders}
            folder={folder}
            onFolder={setFolder}
            onNewFolder={() => setFolderDialog({ folder: null })}
            onRenameFolder={(f) => setFolderDialog({ folder: f })}
            onDeleteFolder={setFolderToDelete}
            query={query}
            onQuery={setQuery}
            sort={sort}
            onSort={setSort}
            showToolbar={projects.length > 0}
          />
        )}

        <BatchActionBar
          count={picked.length}
          folders={folders}
          locked={!!trial}
          onMove={batchMove}
          onDelete={() => (trial
            ? toast.error(t("delete_paid_only"))
            : setConfirmDelete({ batch: true, title: t("selected_count", { n: picked.length }) }))}
          onClear={() => setPicked([])}
        />

        {loading ? (
          <div className="flex justify-center py-24 text-[#707070]">
            <Loader2 className="animate-spin" />
          </div>
        ) : projects.length === 0 ? (
          <div data-testid="empty-projects-state" className="border border-[#2A2A2A] bg-[#0B0B0B] p-16 text-center">
            <Images size={26} strokeWidth={1.25} className="mx-auto text-[#D4AF37]" />
            <h3 className="mt-6 text-2xl tracking-tight">{t("no_projects")}</h3>
            <p className="mt-3 text-sm text-[#A8A8A8]">{t("no_projects_body")}</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="projects-grid">
            {visible.length === 0 && (
              <p className="text-sm text-[#707070] col-span-full" data-testid="no-search-results">{t("no_search_results")}</p>
            )}
            {visible.map((p) => (
              <ProjectCard
                key={p.project_id}
                project={p}
                picked={picked.includes(p.project_id)}
                locked={!!trial}
                showInvoice={isOwner}
                reminding={reminding === p.project_id}
                onPick={togglePick}
                onDelete={(proj) => (trial ? toast.error(t("delete_paid_only")) : setConfirmDelete(proj))}
                onRemind={remind}
                onCopyLink={(proj) => {
                  navigator.clipboard.writeText(`${window.location.origin}/g/${proj.share_token}`);
                  toast.success("Client link copied");
                }}
              />
            ))}
          </div>
        )}
      </main>
      <SupportButton />
      <TrialCountdown trial={trial} />

      {folderDialog && (
        <PromptDialog
          testId="folder-dialog"
          title={folderDialog.folder ? t("rename_folder") : t("new_folder")}
          label={t("folder_name")}
          defaultValue={folderDialog.folder?.name || ""}
          confirmLabel={t("save")}
          onConfirm={saveFolder}
          onClose={() => setFolderDialog(null)}
        />
      )}

      {folderToDelete && (
        <ConfirmDialog
          testId="folder-delete-dialog"
          title={t("delete_folder")}
          body={`${folderToDelete.name} — ${t("folder_delete_note")}`}
          confirmLabel={t("delete_folder")}
          onConfirm={() => removeFolder(folderToDelete)}
          onClose={() => setFolderToDelete(null)}
        />
      )}

      {confirmDelete && (
        <DeleteProjectDialog
          title={confirmDelete.title}
          text={deleteText}
          onText={setDeleteText}
          onConfirm={() => (confirmDelete.batch ? batchDelete() : remove(confirmDelete.project_id))}
          onClose={() => { setConfirmDelete(null); setDeleteText(""); }}
        />
      )}

      {open && (
        <NewProjectDialog
          form={form}
          setForm={setForm}
          templates={templates}
          busy={busy}
          onSubmit={create}
          onClose={() => { setOpen(false); setForm(emptyForm); }}
          onApplyTemplate={applyTemplate}
          onQuotaItem={setQuotaItem}
          presets={packagePresets}
          printCatalog={printCatalog}
        />
      )}
    </div>
  );
}
